/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 23-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22299
 * Purpose      : Load-from scope selector (a trade's tenants + Provider default) for the NAOMI mapping.
 */

import React from 'react';
import { Select, MenuItem, FormControl, InputLabel, ListSubheader, CircularProgress, Box } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import type { TenantOption } from './services/TenantMappingService';

/** Sentinel value for the "Provider default" scope option (vs a real tenantId). */
export const PROVIDER_SCOPE = '__provider__';

interface MappingScopeSelectProps {
  tenants: TenantOption[];
  /** Current scope: a tenantId, PROVIDER_SCOPE, or '' (none). */
  value: string;
  loading?: boolean;
  onChange: (scope: string) => void;
  /** Field label + placeholder verb (e.g. "Load from", "Save to"). Defaults to "Save to". */
  label?: string;
}

/**
 * Single scope selector for the Mapping tab: the trade's adopted tenants (primary), plus
 * "Provider default" as an extra option. Selecting a scope drives which tenant/provider the mapped
 * task template is loaded from and saved to. Templates are never shared between tenants, so exactly
 * one scope is active at a time.
 */
export function MappingScopeSelect({ tenants, value, loading, onChange, label = 'Save to' }: MappingScopeSelectProps) {
  const handleChange = (e: SelectChangeEvent<string>) => onChange(e.target.value);
  const labelId = `mapping-scope-label-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 240 }}>
        {/* shrink: with displayEmpty, MUI only floats the label when the Select's value is
            non-empty — with value="" it instead rests in place, directly on top of the
            placeholder text renderValue draws for the empty state. shrink keeps it floated
            above the field always, so the two never overlap. */}
        <InputLabel id={labelId} shrink>{label}</InputLabel>
        <Select<string>
          labelId={labelId}
          label={label}
          value={value}
          onChange={handleChange}
          disabled={loading}
          displayEmpty
          notched
          renderValue={(selected) => {
            if (!selected) return <em style={{ color: '#888' }}>Select tenant or provider…</em>;
            if (selected === PROVIDER_SCOPE) return 'Provider default';
            return tenants.find(t => t.tenantId === selected)?.name ?? selected;
          }}
        >
          <MenuItem value="" disabled>
            <em>Select tenant or provider…</em>
          </MenuItem>
          {tenants.length > 0 && <ListSubheader>Tenant</ListSubheader>}
          {tenants.map(t => (
            <MenuItem key={t.tenantId} value={t.tenantId}>{t.name}</MenuItem>
          ))}
          <ListSubheader>Provider</ListSubheader>
          <MenuItem value={PROVIDER_SCOPE}>Provider default</MenuItem>
        </Select>
      </FormControl>
      {loading && <CircularProgress size={16} thickness={4} />}
    </Box>
  );
}
